import { memo } from 'react';
import { Handle, Position, NodeProps, type Node } from '@xyflow/react';
import { CpuChipIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { DefinitionNodeData } from '../../../utils/definitionTransform';
import { AgentNode as AgentNodeType } from '../../../types/definition';
import { ValidationError } from '../../../utils/definitionValidation';

interface ExtendedNodeData extends DefinitionNodeData {
  validationErrors?: ValidationError[];
}

function AgentNodeComponent({ data, selected }: NodeProps<Node<ExtendedNodeData>>) {
  const node = data.originalNode as AgentNodeType;
  const errors = data.validationErrors?.filter(e => e.severity === 'error') || [];
  const warnings = data.validationErrors?.filter(e => e.severity === 'warning') || [];
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;
  const hasAgentId = node.agentId && node.agentId.length > 0;
  const hasOutput = node.output && Object.keys(node.output).length > 0;

  const getBorderColor = () => {
    if (hasErrors) return 'border-red-500';
    if (selected) return 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800';
    if (!hasAgentId) return 'border-blue-400 dark:border-blue-600 border-dashed';
    return 'border-blue-400 dark:border-blue-600';
  };

  return (
    <div
      className={`
        px-4 py-3 rounded-lg border-2 bg-white dark:bg-gray-800 shadow-sm min-w-[160px]
        ${getBorderColor()}
      `}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !bg-blue-500 !border-2 !border-white dark:!border-gray-800"
      />

      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-md ${hasErrors ? 'bg-red-100 dark:bg-red-900' : 'bg-blue-100 dark:bg-blue-900'}`}>
          <CpuChipIcon className={`w-4 h-4 ${hasErrors ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate flex items-center gap-1">
            {data.label}
            {(hasErrors || hasWarnings) && (
              <ExclamationCircleIcon className={`w-4 h-4 ${hasErrors ? 'text-red-500' : 'text-amber-500'}`} />
            )}
          </div>
          {node.alias && node.alias !== data.label && (
            <div className="text-xs text-blue-600 dark:text-blue-400 truncate">
              {node.alias}
            </div>
          )}
          {!hasAgentId && !hasErrors && (
            <div className="text-xs text-amber-600 dark:text-amber-400">
              No agent selected
            </div>
          )}
        </div>
      </div>

      <div className="mt-1.5 flex items-center gap-1 flex-wrap">
        {node.entry && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
            Entry
          </span>
        )}
        {hasOutput && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300">
            Exit
          </span>
        )}
      </div>

      {hasErrors && (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400 truncate">
          {errors[0].message}
        </div>
      )}

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !bg-blue-500 !border-2 !border-white dark:!border-gray-800"
      />
    </div>
  );
}

export const AgentNode = memo(AgentNodeComponent);
